import actions from './actions';
import { DataService } from '../../config/dataService/dataService';
import HttpStatus from '../../utility/status.enum';

const { getItemsBegin, getItemsSuccess, getItemsErr } = actions;

const threadListAPI = `/get-thread`;
const messageListAPI = `/get-message`;
const messagePostAPI = `/post-message`;
const aiEnableDisableAPI = `/ai-enable-disable`;
const threadDetailAPI = `/thread-details`;
const markAsReadAPI = `mark-as-read`;
const guestDetailAPI = `/message-thread-reservation-detail/`
const guestProgressLinkAPI = `/message-thread-reservation-links/`
const scheduledMessageSkipAPI = `/scheduled-message-skip`
const unreadMessageAPI = `/get-unread-count`
const aiSuggestionsAPI = `/get-ai-suggestions`
const resendMessageAPI = `/resend-message`
const toggleThreadFlagAPI = `/toggle-flag`;
const messageAiSourcesAPI = `/get-message-ai-sources/`;

/**
 * @param {Object} values - POST body for /get-thread. Supports an additive,
 *   optional `quick_filter` string (Unified Inbox quick-filter pills) — omitted
 *   entirely when the caller doesn't set it, so existing callers are unaffected.
 */
const getThreadList = (values) => {
  return async (dispatch) => {
    dispatch(getItemsBegin());
    try {
      const response = await DataService.post(threadListAPI, values);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(getItemsErr(response?.data?.message));
      } else {
        dispatch(getItemsSuccess(response?.data));
      }
      return response?.data;
    } catch (err) {
      dispatch(getItemsErr(err));
    }
  };
};

const getMessages = (values) => {
  return async (dispatch) => {
    dispatch(getItemsBegin());
    try {
      const response = await DataService.post(messageListAPI, values);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(getItemsErr(response?.data?.message));
      } else {
        dispatch(getItemsSuccess(response?.data));
      }
      return response?.data;
    } catch (err) {
      dispatch(getItemsErr(err));
    }
  };
};

const postMessage = (values) => {
  return async (dispatch) => {
    dispatch(getItemsBegin());
    try {
      const response = await DataService.post(messagePostAPI, values);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(getItemsErr(response?.data?.message));
      } else {
        dispatch(getItemsSuccess(response?.data));
      }
      return response?.data?.result;
    } catch (err) {
      dispatch(getItemsErr(err));
    }
  };
};

const aiEnableDisable = (values) => {
  return async (dispatch) => {
    dispatch(getItemsBegin());
    try {
      const response = await DataService.post(aiEnableDisableAPI, values);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(getItemsErr(response?.data?.message));
      } else {
        dispatch(getItemsSuccess(response?.data));
      }
      return response?.data;
    } catch (err) {
      dispatch(getItemsErr(err));
    }
  };
};

const getThreadDetail = (values) => {
  return async (dispatch) => {
    dispatch(getItemsBegin());
    try {
      const response = await DataService.post(threadDetailAPI, values);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(getItemsErr(response?.data?.message));
      } else {
        dispatch(getItemsSuccess(response?.data));
      }
      return response?.data?.result;
    } catch (err) {
      dispatch(getItemsErr(err));
    }
  };
};

/**
 * @param {Object} values - { thread_slug, request_from, action? }. `action` is an
 *   additive, optional 'read'|'unread' param (mirrors the backend's new param) —
 *   omitted, the backend defaults to marking read as before.
 *   NOTE: returns the full response.data (not .result) so callers can check
 *   `.status` — existing callers that ignore the resolved value are unaffected.
 */
const markAsRead = (values) => {
  return async (dispatch) => {
    dispatch(getItemsBegin());
    try {
      const response = await DataService.post(markAsReadAPI, values);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(getItemsErr(response?.data?.message));
      } else {
        dispatch(getItemsSuccess(response?.data));
      }
      return response?.data;
    } catch (err) {
      dispatch(getItemsErr(err));
    }
  };
};

const toggleThreadFlag = (values) => {
  return async (dispatch) => {
    dispatch(getItemsBegin());
    try {
      const response = await DataService.post(toggleThreadFlagAPI, values);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(getItemsErr(response?.data?.message));
      } else {
        dispatch(getItemsSuccess(response?.data));
      }
      return response?.data;
    } catch (err) {
      dispatch(getItemsErr(err));
    }
  };
};


const guestDetail = (value) => {
  return async (dispatch) => {
    dispatch(getItemsBegin());
    try {
      const response = await DataService.get(guestDetailAPI + value);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(getItemsErr(response?.data?.message));
      } else {
        dispatch(getItemsSuccess(response?.data));
      }
      return response?.data?.result;
    } catch (err) {
      dispatch(getItemsErr(err));
    }
  };
};

/**
 * One guest-progress link for a booking. `linkType` is the value the panel already has in
 * guest_progress[].link.type — rental_agreement, guest_ids or damage_waiver — so only the
 * link the host actually opened is built server side.
 */
const guestProgressLink = (bookingSlug, linkType) => {
  return async (dispatch) => {
    dispatch(getItemsBegin());
    try {
      const response = await DataService.get(`${guestProgressLinkAPI}${bookingSlug}?type=${linkType}`);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(getItemsErr(response?.data?.message));
        return null;
      }
      dispatch(getItemsSuccess(response?.data));
      return response?.data?.result;
    } catch (err) {
      dispatch(getItemsErr(err));
      return null;
    }
  };
};

/**
 * Skip or unskip one automated message of a booking from the guest panel.
 * @param {Object} values - { booking_slug, message_slug, action: 'skip' | 'unskip' }
 * Returns the full response.data so the caller can show its message on success and on error.
 */
const skipScheduledMessage = (values) => {
  return async (dispatch) => {
    dispatch(getItemsBegin());
    try {
      const response = await DataService.post(scheduledMessageSkipAPI, values);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(getItemsErr(response?.data?.message));
      } else {
        dispatch(getItemsSuccess(response?.data));
      }
      return response?.data;
    } catch (err) {
      dispatch(getItemsErr(err));
    }
  };
};

const getUnreadMessage = () => {
  return async (dispatch) => {
    dispatch(getItemsBegin());
    try {
      const response = await DataService.get(unreadMessageAPI);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(getItemsErr(response?.data?.message));
      } else {
        dispatch(getItemsSuccess(response?.data));
      }
      return response?.data;
    } catch (err) {
      dispatch(getItemsErr(err));
    }
  };
};

const getAiSuggestions = (values) => {
  return async (dispatch) => {
    dispatch(getItemsBegin());
    try {
      /*return {
          "status": 200,
          "result": [
              "Check In is at 3:00 PM. If you'd like an early check In, it's available for purchase through your reservation page!",
              "Standard check In starts at 3:00 PM. Early check In may be available — you can request it via your Happy Guest portal.",
              "Our check In time is 3:00 PM. Early check In is subject to availability. Let me check and get back to you shortly!",
          ]
      };*/
      
      const response = await DataService.post(aiSuggestionsAPI, values);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(getItemsErr(response?.data?.message));
      } else {
        dispatch(getItemsSuccess(response?.data));
      }
      return response?.data;
      
    } catch (err) {
      dispatch(getItemsErr(err));
    }
  };
};

const resendMessage = (values) => {
  return async (dispatch) => {
    dispatch(getItemsBegin());
    try {
      const response = await DataService.post(resendMessageAPI, values);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(getItemsErr(response?.data?.message));
      } else {
        dispatch(getItemsSuccess(response?.data));
      }
      return response?.data;
    } catch (err) {
      dispatch(getItemsErr(err));
    }
  };
};

// KB articles a specific AI reply was generated from, enriched (title/answer/visibility)
// for the sources dialog opened from the (i) icon next to "Sent by HGAI at …".
const getMessageAiSources = (messageSlug) => {
  return async (dispatch) => {
    dispatch(getItemsBegin());
    try {
      const response = await DataService.get(`${messageAiSourcesAPI}${messageSlug}`);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(getItemsErr(response?.data?.message));
      } else {
        dispatch(getItemsSuccess(response?.data));
      }
      return response?.data;
    } catch (err) {
      dispatch(getItemsErr(err));
    }
  };
};

export { getThreadList, getMessages, postMessage, getThreadDetail, aiEnableDisable, markAsRead, guestDetail, guestProgressLink, skipScheduledMessage, getUnreadMessage, getAiSuggestions, resendMessage, toggleThreadFlag, getMessageAiSources };
